import React, {ReactNode} from 'react'
import styled from '@emotion/styled'

import {cssTheme} from '../../../themes'
import {linariaMq} from '../../../utils/breakpoints'
import {Icon} from '../Icon'
import {Text, TextVariant} from '../Text'

interface Props {
  /** Pass through classname to allow styles overrides */
  className?: string
  /** Identify the element for selection in integration tests, FullStory, etc. */
  dataId?: string
  /** Whether the checkbox is checked or not */
  checked: boolean
  /** Whether the checkbox is disabled or not */
  disabled?: boolean
  /** Whether the input has an error */
  hasError?: boolean
  /** Content to render in label next to the checkbox */
  label?: ReactNode
  /** Name used for html input element */
  name: string
  /** The Checkbox change handler. Fires when user change state of Checkbox */
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  /** Value used for html input element */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value?: any
  /** Use larger touch area */
  hasTouchArea?: boolean
  /** Label size */
  labelSize?: Extract<TextVariant, 'bodyM' | 'bodyS'>
}

const CHECKBOX_SIZE = 20
const LABEL_MARGIN_LEFT = 10
const TOUCHAREA_SIZE_LG = 12

const CheckboxElement = styled.div`
  display: flex;
`

export const CheckboxContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-inline-start: ${LABEL_MARGIN_LEFT}px;
  color: ${cssTheme.colors.inputCheckbox.unselected.default.content};
`
export const CheckboxIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${CHECKBOX_SIZE}px;
  min-width: ${CHECKBOX_SIZE}px;
  height: ${CHECKBOX_SIZE}px;
  background-color: ${cssTheme.colors.inputCheckbox.unselected.default
    .background};
  border: 1px solid ${cssTheme.colors.inputCheckbox.unselected.default.border};
  border-radius: ${cssTheme.layout.radius.md};
  color: ${cssTheme.colors.inputCheckbox.unselected.default.icon};
  cursor: pointer;
  transition:
    background-color 0.15s ease-in-out,
    border-color 0.15s ease-in-out,
    color 0.15s ease-in-out,
    transform 0.15s ease-in-out;

  svg {
    display: inline-flex;
    opacity: 0;
    visibility: hidden;
    transform: scale(0.6);
  }
`

export const CheckboxInput = styled.input`
  display: block;
  position: absolute;
  top: 0;
  left: ${CHECKBOX_SIZE + TOUCHAREA_SIZE_LG}px;
  opacity: 0;
  width: 0;
  height: 0;

  &:focus-visible + ${CheckboxIcon} {
    outline: 2px solid ${cssTheme.colors.border.interactive.primaryActive};
    outline-offset: 2px;
  }

  &:checked + ${CheckboxIcon} {
    background-color: ${cssTheme.colors.inputCheckbox.selected.default
      .background};
    border-color: ${cssTheme.colors.inputCheckbox.selected.default.border};

    svg {
      opacity: 1;
      visibility: visible;
      transform: translateY(0);
    }
  }

  &[data-field-error='true'] + ${CheckboxIcon} {
    background-color: ${cssTheme.colors.inputCheckbox.error.default.background};
    border-color: ${cssTheme.colors.inputCheckbox.error.default.border};
    color: ${cssTheme.colors.inputCheckbox.error.default.icon};
  }

  &:disabled + ${CheckboxIcon} {
    background-color: ${cssTheme.colors.inputCheckbox.disabled.background};
    border-color: ${cssTheme.colors.inputCheckbox.disabled.border};
    color: ${cssTheme.colors.inputCheckbox.disabled.icon};
    cursor: not-allowed;
  }

  &:disabled ~ ${CheckboxContent} {
    cursor: not-allowed;
    color: ${cssTheme.colors.inputCheckbox.disabled.content};
  }
`

export const CheckboxBody = styled.label<{hasTouchArea: boolean}>`
  display: flex;
  align-items: center;
  position: relative;
  padding: ${({hasTouchArea}) => (hasTouchArea ? TOUCHAREA_SIZE_LG : 0)}px;

  ${linariaMq.desktopXs} {
    &:hover {
      ${CheckboxInput}:not(:disabled):not(:active) {
        & + ${CheckboxIcon} {
          border-color: ${cssTheme.colors.inputCheckbox.unselected.hover
            .border};
        }

        &:checked + ${CheckboxIcon} {
          background-color: ${cssTheme.colors.inputCheckbox.selected.hover
            .background};
          border-color: ${cssTheme.colors.inputCheckbox.selected.hover.border};
        }

        &[data-field-error='true'] + ${CheckboxIcon} {
          background-color: ${cssTheme.colors.inputCheckbox.error.hover
            .background};
          border-color: ${cssTheme.colors.inputCheckbox.error.hover.border};
          color: ${cssTheme.colors.inputCheckbox.error.hover.icon};
        }
      }
    }
  }

  @supports selector(:has(input:disabled)) {
    cursor: pointer;
    :has(input:disabled) {
      cursor: not-allowed;
    }
  }
`

const StyledText = styled(Text)`
  line-height: ${CHECKBOX_SIZE}px !important;
`

export const Checkbox = ({
  checked,
  value,
  name,
  dataId,
  onChange,
  label,
  disabled,
  hasError = false,
  hasTouchArea = false,
  className,
  labelSize = 'bodyS'
}: Props) => (
  <CheckboxElement className={className}>
    <CheckboxBody hasTouchArea={hasTouchArea}>
      <CheckboxInput
        type="checkbox"
        name={name}
        data-id={dataId}
        checked={checked}
        value={value}
        disabled={disabled}
        data-field-error={hasError}
        onChange={onChange}
      />
      <CheckboxIcon>
        <Icon name="Checkmark" size="sm" />
      </CheckboxIcon>
      {Boolean(label) && (
        <CheckboxContent>
          <StyledText variant={labelSize}>{label}</StyledText>
        </CheckboxContent>
      )}
    </CheckboxBody>
  </CheckboxElement>
)
